import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";

/**
 * Bouton micro réutilisable utilisant l'API SpeechRecognition (Chrome/Edge/Safari).
 * Utilisation :
 *   <VoiceMicButton onTranscript={(text) => setInput(text)} append />
 *
 * Props :
 * - onTranscript(text) : callback à chaque résultat (interim + final)
 * - currentValue : valeur actuelle du champ (pour append intelligent)
 * - append : si true, ajoute à currentValue. Sinon remplace.
 * - lang : par défaut "fr-FR"
 * - testid : data-testid
 */
export default function VoiceMicButton({
  onTranscript,
  currentValue = "",
  append = true,
  lang = "fr-FR",
  testid = "voice-mic-button",
  size = "sm",
}) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);
  const baseValueRef = useRef("");

  useEffect(() => {
    setSupported(
      typeof window !== "undefined" &&
        !!(window.SpeechRecognition || window.webkitSpeechRecognition)
    );
    return () => {
      try { recognitionRef.current?.stop(); } catch {}
    };
  }, []);

  const start = () => {
    if (!supported) return;
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    baseValueRef.current = append && currentValue ? currentValue.trim() + " " : "";
    rec.onresult = (e) => {
      const transcript = Array.from(e.results)
        .map((r) => r[0].transcript)
        .join(" ");
      onTranscript(baseValueRef.current + transcript);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  };

  if (!supported) return null;

  const sizeCls = size === "lg" ? "h-11 w-11" : size === "md" ? "h-9 w-9" : "h-8 w-8";
  const iconCls = size === "lg" ? "h-5 w-5" : "h-4 w-4";

  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      title={listening ? "Arrêter la dictée" : "Dicter avec le micro"}
      className={`${sizeCls} flex items-center justify-center rounded-sm border transition-colors ${
        listening
          ? "bg-red-600 hover:bg-red-700 border-red-600 text-white"
          : "bg-white hover:bg-slate-50 border-slate-300 text-slate-700"
      }`}
      data-testid={testid}
    >
      {listening ? (
        <span className="relative flex">
          <MicOff className={iconCls} />
          <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
        </span>
      ) : (
        <Mic className={iconCls} />
      )}
    </button>
  );
}
